import { z } from 'zod';
import type { FeatureFlagValue } from './feature-flags.constants';
import { FEATURE_FLAG_NAMES } from './feature-flags';

export const FeatureFlagTypeSchema = z.enum([
  'BOOLEAN',
  'NUMBER',
  'STRING',
  'TIMESTAMP',
]);

export type FeatureFlagType = z.infer<typeof FeatureFlagTypeSchema>;

export const TenantFeatureFlagSchema = z.enum(FEATURE_FLAG_NAMES);

export const TenantFeatureFlagValueSchema = z.union([
  z.boolean(),
  z.number(),
  z.string(),
  z.null(),
]) as z.ZodType<FeatureFlagValue>;

export const TenantFeatureFlagsSchema = z
  .record(TenantFeatureFlagSchema, TenantFeatureFlagValueSchema)
  .describe(
    'Feature flags map keyed by feature name with values of any supported type',
  );

export const TenantFeatureFlagsResponseSchema = z
  .object({
    id: z.string().describe('Record id'),
    tenantId: z.string().describe('Tenant identifier'),
    featureFlags: TenantFeatureFlagsSchema.describe('Tenant feature flags'),
    createdAt: z.coerce.date().describe('Creation timestamp'),
    updatedAt: z.coerce.date().describe('Last update timestamp'),
  })
  .describe('Tenant feature flags response');

export const UpdateTenantFeatureFlagsSchema = z
  .object({
    featureFlags: z
      .partialRecord(TenantFeatureFlagSchema, TenantFeatureFlagValueSchema)
      .describe('Feature flags to update for the tenant'),
  })
  .describe('Feature flags update payload');

export const ApplyToAllTenantsResponseSchema = z
  .object({
    updatedCount: z.number().describe('Number of tenants updated'),
  })
  .describe('Apply to all tenants response');

export const DeleteTenantFeatureFlagRulesResponseSchema = z
  .object({
    deletedCount: z.int().describe('Number of rules deleted'),
  })
  .describe('Delete tenant feature flag rules response');

export const FeatureFlagRuleTargetTypeSchema = z.enum([
  'tenant',
  'application',
  'user',
  'segment',
]);

export const FeatureFlagUiTargetTypeSchema = z.enum(['tenant', 'application']);
export type FeatureFlagUiTargetType = z.infer<
  typeof FeatureFlagUiTargetTypeSchema
>;

export const FEATURE_FLAG_UI_TARGET_TYPES =
  FeatureFlagUiTargetTypeSchema.options;

export const FeatureFlagTargetingApplicationSchema = z.object({
  id: z.string().describe('Application id'),
  name: z.string().describe('Application name'),
  tenantId: z.string().nullable().describe('Owning tenant id'),
});
export type FeatureFlagTargetingApplication = z.infer<
  typeof FeatureFlagTargetingApplicationSchema
>;

export const TARGETING_APPLICATIONS_PAGE_SIZE = 100;

export const ListTargetingApplicationsQuerySchema = z.object({
  search: z.string().optional().describe('Filter applications by name or id'),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(TARGETING_APPLICATIONS_PAGE_SIZE)
    .prefault(TARGETING_APPLICATIONS_PAGE_SIZE)
    .describe('Maximum applications to return'),
  offset: z.coerce
    .number()
    .int()
    .min(0)
    .prefault(0)
    .describe('Applications to skip'),
});
export type ListTargetingApplicationsQuery = z.infer<
  typeof ListTargetingApplicationsQuerySchema
>;

export const ListTargetingApplicationsResponseSchema = z.object({
  applications: z
    .array(FeatureFlagTargetingApplicationSchema)
    .describe('Applications available for targeting rules'),
  total: z.int().describe('Total matching applications'),
  limit: z.int().describe('Applied page size'),
  offset: z.int().describe('Applied page offset'),
});
export type ListTargetingApplicationsResponse = z.infer<
  typeof ListTargetingApplicationsResponseSchema
>;
export type FeatureFlagRuleTargetType = z.infer<
  typeof FeatureFlagRuleTargetTypeSchema
>;

export const FeatureFlagRuleSchema = z.object({
  id: z.string().describe('Rule id'),
  flagKey: TenantFeatureFlagSchema.describe('Flag key'),
  rank: z.int().describe('Rule order'),
  targetType: FeatureFlagRuleTargetTypeSchema.describe('Target type'),
  targetId: z.string().describe('Target id (e.g. tenantId)'),
  value: TenantFeatureFlagValueSchema.describe(
    'Served value when rule matches',
  ),
});
export type FeatureFlagRule = z.infer<typeof FeatureFlagRuleSchema>;

export const FeatureFlagRuleUpdateSchema = FeatureFlagRuleSchema.extend({
  id: z.string().optional().describe('Rule id (omit for new rules)'),
});
export type FeatureFlagRuleUpdate = z.infer<typeof FeatureFlagRuleUpdateSchema>;

export const FlagConfigResponseSchema = z.object({
  flagKey: TenantFeatureFlagSchema.describe('Flag key'),
  defaultValue: TenantFeatureFlagValueSchema.describe(
    'Default / everyone value',
  ),
  rules: z.array(FeatureFlagRuleSchema).describe('Ordered targeting rules'),
  supportedTargetTypes: z
    .array(FeatureFlagRuleTargetTypeSchema)
    .describe('Target types allowed for this flag'),
});
export type FlagConfigResponse = z.infer<typeof FlagConfigResponseSchema>;

export const UpdateFlagConfigSchema = z.object({
  defaultValue:
    TenantFeatureFlagValueSchema.optional().describe('Default value'),
  rules: z
    .array(FeatureFlagRuleUpdateSchema)
    .optional()
    .describe(
      'Rules (include id to update existing, omit id for new; others are removed)',
    ),
});
export type UpdateFlagConfig = z.infer<typeof UpdateFlagConfigSchema>;

export const FlagListItemSchema = z.object({
  flagKey: TenantFeatureFlagSchema.describe('Flag key'),
  defaultValue: TenantFeatureFlagValueSchema.describe('Default value'),
  type: FeatureFlagTypeSchema.optional().describe('Flag value type'),
  supportedTargetTypes: z
    .array(FeatureFlagRuleTargetTypeSchema)
    .describe('Target types allowed for this flag'),
});
export type FlagListItem = z.infer<typeof FlagListItemSchema>;

export const ListFlagsResponseSchema = z.array(FlagListItemSchema);
